import React from 'react';
import { motion } from 'framer-motion';
import { Target, Lightbulb, Settings, TrendingUp } from 'lucide-react';
export function AboutPage() {
  return <div className="min-h-screen bg-sandev-bg" data-id="element-88">
      {/* Header */}
      <section className="pt-24 pb-16 bg-sandev-card border-b border-sandev-border" data-id="element-89">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-90">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-3xl" data-id="element-91">
            <h1 className="text-4xl md:text-5xl font-bold text-sandev-text mb-6" data-id="element-92">
              About SanDev Industries
            </h1>
            <p className="text-xl text-sandev-muted leading-relaxed" data-id="element-93">
              We are bridging the gap between concept and reality. SanDev
              Industries was founded on a simple premise: industrial-quality
              prototyping shouldn't require industrial-scale budgets.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20" data-id="element-94">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-95">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-96">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} data-id="element-97">
              <h2 className="text-3xl font-bold text-sandev-text mb-6 flex items-center gap-3" data-id="element-98">
                <Lightbulb className="text-sandev-accent" data-id="element-99" />
                The Business Idea
              </h2>
              <div className="space-y-6 text-sandev-muted text-lg leading-relaxed" data-id="element-100">
                <p data-id="element-101">
                  In traditional manufacturing, creating a single prototype
                  often involves expensive tooling, CNC machining setup costs,
                  or minimum order quantities. This creates a massive barrier to
                  entry for rapid iteration.
                </p>
                <p data-id="element-102">
                  SanDev Industries leverages advanced additive manufacturing
                  (3D printing) to bypass these traditional constraints. By
                  utilizing both Fused Deposition Modeling (FDM) and high-detail
                  Resin printing, we offer a cheaper, faster alternative that
                  doesn't compromise on quality.
                </p>
                <p data-id="element-103">
                  Our service is designed specifically for industrial and
                  manufacturing companies who need to hold a physical part in
                  their hands, test its fit, or verify its form before
                  committing thousands of dollars to mass production tooling.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="bg-sandev-card border border-sandev-border rounded-2xl p-8 shadow-2xl relative overflow-hidden" data-id="element-104">
              <div className="absolute top-0 right-0 w-64 h-64 bg-sandev-accent/5 rounded-full blur-3xl -z-10 translate-x-1/2 -translate-y-1/2" data-id="element-105"></div>
              <div className="aspect-video bg-sandev-bg rounded-xl border border-sandev-border flex items-center justify-center mb-8 overflow-hidden relative" data-id="element-106">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20" data-id="element-107"></div>
                <img src="https://cdn.magicpatterns.com/uploads/kMbAgddL1UiprjndkWyf1u/SanDev_Logo_v2_crystal_Core.png" alt="SanDev Logo" className="w-1/2 h-1/2 object-contain drop-shadow-[0_0_15px_rgba(0,0,0,0.5)]" data-id="element-108" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-109">
                <div className="bg-sandev-bg p-4 rounded-lg border border-sandev-border" data-id="element-110">
                  <div className="text-2xl font-bold text-sandev-accent mb-1" data-id="element-111">
                    10x
                  </div>
                  <div className="text-sm text-sandev-muted" data-id="element-112">
                    Faster than traditional tooling
                  </div>
                </div>
                <div className="bg-sandev-bg p-4 rounded-lg border border-sandev-border" data-id="element-113">
                  <div className="text-2xl font-bold text-sandev-accent mb-1" data-id="element-114">
                    -80%
                  </div>
                  <div className="text-sm text-sandev-muted" data-id="element-115">
                    Average cost reduction
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Mission & Vision */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-24" data-id="element-116">
            <div className="bg-sandev-card border border-sandev-border rounded-xl p-8" data-id="element-117">
              <Target className="text-sandev-accent w-10 h-10 mb-6" data-id="element-118" />
              <h3 className="text-2xl font-bold text-sandev-text mb-4" data-id="element-119">
                Our Mission
              </h3>
              <p className="text-sandev-muted leading-relaxed" data-id="element-120">
                To empower engineers, designers, and manufacturers to iterate
                freely by providing accessible, high-quality, and rapid
                prototyping services. We believe that the ability to fail fast
                and iterate cheaply is the key to superior final products.
              </p>
            </div>
            <div className="bg-sandev-card border border-sandev-border rounded-xl p-8" data-id="element-121">
              <TrendingUp className="text-sandev-accent w-10 h-10 mb-6" data-id="element-122" />
              <h3 className="text-2xl font-bold text-sandev-text mb-4" data-id="element-123">
                Our Vision
              </h3>
              <p className="text-sandev-muted leading-relaxed" data-id="element-124">
                To become the go-to rapid prototyping partner for the
                manufacturing sector, continuously expanding our material
                capabilities and technological offerings to bridge the gap
                between digital design and physical reality.
              </p>
            </div>
          </div>

          {/* Capabilities */}
          <div data-id="element-125">
            <h2 className="text-3xl font-bold text-sandev-text mb-10 text-center" data-id="element-126">
              Technical Capabilities
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-127">
              <div className="bg-sandev-bg border border-sandev-border rounded-xl p-6" data-id="element-128">
                <Settings className="text-sandev-accent mb-4" size={24} data-id="element-129" />
                <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-130">
                  Industrial FDM
                </h4>
                <p className="text-sm text-sandev-muted" data-id="element-131">
                  High-temperature capable machines allowing for
                  engineering-grade materials like Nylon, Polycarbonate, and
                  Carbon Fiber composites. Large build volumes for substantial
                  parts.
                </p>
              </div>
              <div className="bg-sandev-bg border border-sandev-border rounded-xl p-6" data-id="element-132">
                <Settings className="text-sandev-accent mb-4" size={24} data-id="element-133" />
                <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-134">
                  MSLA Resin
                </h4>
                <p className="text-sm text-sandev-muted" data-id="element-135">
                  High-resolution monochrome screens delivering XY resolutions
                  down to 22 microns. Perfect for intricate details, smooth
                  surfaces, and specialized resin applications.
                </p>
              </div>
              <div className="bg-sandev-bg border border-sandev-border rounded-xl p-6" data-id="element-136">
                <Settings className="text-sandev-accent mb-4" size={24} data-id="element-137" />
                <h4 className="text-lg font-bold text-sandev-text mb-2" data-id="element-138">
                  Post-Processing
                </h4>
                <p className="text-sm text-sandev-muted" data-id="element-139">
                  Complete finishing services including support removal, UV
                  curing, sanding, priming, and threaded insert installation for
                  functional assemblies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}